import React from 'react';

import { Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import ClassesContainer from './ClassesContainer';
import ClassesDateRange from './ClassesDateRange';
import ClassFilters from './ClassFilters';
import CopyUrlButton from './CopyUrlButton';
import Instructors from './Instructors';
import LoadingTableRows from './LoadingTableRows';
import { type Direction, DirectionOption, type SortBy, SortOption, Status } from './types';
import { useTablePagination, TablePagination, PaginationContainer } from '../../../../components/TablePagination';
import StyledFormTitle from '../../styles/form-title';
import { ActionsTableCell, StyledTableCell } from '../../styles/table-styles';

const StyledTable = styled(Table)`
    margin-bottom: 2rem;
`;

const Title = styled(StyledFormTitle)`
    margin-bottom: 1rem;
`;

const ClassLink = styled(Link)`
    color: ${(props) => props.theme.colors.primary.main};
`;

const ClassesTable: React.FC = () => {
    const { filteredClasses, statuses, sortBy, setSortBy, sortDirection, setSortDirection } =
        useContainer(ClassesContainer);
    const { t } = useTranslation('portal');
    const { path } = useRouteMatch();

    const handleSortSelection = ({ direction, _sortBy }: { direction: Direction; _sortBy: SortBy }) => {
        if (sortBy !== _sortBy) {
            setSortDirection(DirectionOption.ASCENDING);
            setSortBy(_sortBy);
            return;
        }

        setSortBy(_sortBy);

        if (!direction) setSortDirection(DirectionOption.ASCENDING);
        if (direction === DirectionOption.ASCENDING) setSortDirection(DirectionOption.DESCENDING);
        if (direction === DirectionOption.DESCENDING) setSortDirection(undefined);
    };

    const { onRowsPerPageChange, paginatedValues, onPageChange, rowsPerPage, setPage, count, page } =
        useTablePagination(filteredClasses);

    return (
        <>
            <Title>{t('classes-page.title')}</Title>
            <ClassFilters setPage={setPage} />
            <StyledTable>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <Tooltip arrow title={t('classes-page.sort')}>
                                <TableSortLabel
                                    active={sortBy === SortOption.NAME && !!sortDirection}
                                    direction={sortDirection}
                                    onClick={() =>
                                        handleSortSelection({ direction: sortDirection, _sortBy: SortOption.NAME })
                                    }
                                >
                                    {t('classes-page.name-with-count', { classCount: filteredClasses.length })}
                                </TableSortLabel>
                            </Tooltip>
                        </TableCell>
                        <TableCell>
                            <Tooltip arrow title={t('classes-page.sort')}>
                                <TableSortLabel
                                    active={sortBy === SortOption.INSTRUCTOR && !!sortDirection}
                                    direction={sortDirection}
                                    onClick={() =>
                                        handleSortSelection({
                                            direction: sortDirection,
                                            _sortBy: SortOption.INSTRUCTOR
                                        })
                                    }
                                >
                                    {t('classes-page.instructor')}
                                </TableSortLabel>
                            </Tooltip>
                        </TableCell>
                        <TableCell>
                            <Tooltip arrow title={t('classes-page.sort')}>
                                <TableSortLabel
                                    active={sortBy === SortOption.DATES && !!sortDirection}
                                    direction={sortDirection}
                                    onClick={() =>
                                        handleSortSelection({ direction: sortDirection, _sortBy: SortOption.DATES })
                                    }
                                >
                                    {t('classes-page.dates')}
                                </TableSortLabel>
                            </Tooltip>
                        </TableCell>
                        <TableCell>{t('classes-page.class-link')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {statuses.fetchClasses === Status.PENDING ? <LoadingTableRows /> : null}
                    {paginatedValues.map((classRecord) => (
                        <TableRow key={classRecord.class_uuid}>
                            <StyledTableCell>
                                <ClassLink
                                    to={`${path}/${classRecord.class_uuid}?className=${encodeURIComponent(
                                        classRecord.class_name
                                    )}`}
                                >
                                    {classRecord.class_name}
                                </ClassLink>
                            </StyledTableCell>

                            <StyledTableCell>
                                <Instructors instructors={classRecord.instructors} />
                            </StyledTableCell>
                            <StyledTableCell>
                                <ClassesDateRange start={classRecord.starting_at} end={classRecord.ending_at} />
                            </StyledTableCell>
                            <ActionsTableCell>
                                <CopyUrlButton classUuid={classRecord.class_uuid} />
                            </ActionsTableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </StyledTable>
            <PaginationContainer>
                <TablePagination
                    count={count}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={onPageChange}
                    onRowsPerPageChange={onRowsPerPageChange}
                />
            </PaginationContainer>
        </>
    );
};

export default ClassesTable;
